/** @file Skips mirrored inherit keys that would turn a two-bone IK child off "normal" (§6.5). */
import type { AnimationDoc, Doc, Id, IkConstraintDoc } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';

/** The two-bone IK whose second bone is `bone`, when `baked` keys that bone's inherit off "normal"; else undefined. */
function bentChild(doc: Doc, baked: AnimationDoc, bone: Id): IkConstraintDoc | undefined {
  const keys = baked.bones[bone]?.inherit ?? [];
  if (keys.every((k) => k.v === 'normal')) return undefined;
  return Object.values(doc.constraints).find((c) => c.bones[1] === bone);
}

/** Keeps only the "normal" inherit keys of `bone` in `baked` (pruning an emptied timeline); returns how many went. */
function dropBent(baked: AnimationDoc, bone: Id): number {
  const track = baked.bones[bone] ?? {};
  const keys = track.inherit ?? [];
  const kept = keys.filter((k) => k.v === 'normal');
  if (kept.length > 0) track.inherit = kept;
  else Reflect.deleteProperty(track, 'inherit');
  if (Object.keys(track).length === 0) Reflect.deleteProperty(baked.bones, bone);
  return keys.length - kept.length;
}

/**
 * For each (source bone, mirrored bone) pair: when the mirrored bone is the second bone of a two-bone IK, its
 * non-normal inherit keys in `baked` are dropped (validateDoc and animation.setKey refuse them: the solve needs
 * "normal"), "normal" keys stay, and one INFO in setKey's IK notice shape `{ ik, bone, target }` names the
 * constraint, at the source timeline `<at>.bones.<source>.inherit`. Never throws.
 */
export function skipIkChildInherit(doc: Doc, baked: AnimationDoc, pairs: Iterable<readonly [from: Id, to: Id]>, at: string, ctx: CommandContext): void {
  const nameOf = (id: Id): string => doc.bones[id]?.name ?? id;
  for (const [from, to] of pairs) {
    const ik = bentChild(doc, baked, to);
    if (ik === undefined) continue;
    const skipped = dropBent(baked, to);
    const message = `bone "${nameOf(to)}" is the second bone of IK constraint "${ik.name}" and must inherit "normal": ${skipped} mirrored inherit keys from "${nameOf(from)}" were skipped`;
    ctx.warnings.push({ code: 'INFO', message, params: { ik: ik.name, bone: nameOf(to), target: nameOf(ik.target) }, path: `${at}.bones.${from}.inherit` });
  }
}
